// src/components/WalletView.js
import React, { useState, useEffect } from "react";
import {
  Container,
  Paper,
  Typography,
  Button,
  Box,
  Grid,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  Divider,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from "@mui/material";
import {
  ArrowBack,
  Wallet,
  TrendingUp,
  CheckCircle,
  CardGiftcard,
  AccountBalance,
  AccountBalanceWallet,
  AccountTree,
} from "@mui/icons-material";
import AutoFillProgramDashboard from "./AutoFillProgramDashboard";
import CashOutProgramDashboard from "./CashOutProgramDashboard";
import MembershipVoucherPanel from "./MembershipVoucherPanel";
import {
  collection,
  query,
  where,
  getDocs,
  orderBy,
  doc,
  updateDoc,
  addDoc,
  serverTimestamp,
} from "firebase/firestore";

import { db } from "../firebase/config";
import { listCashOutProgramsForUser } from "../services/cashOut/cashOutProgramQueries";
import {
  getCashOutTransferToRoyaltyEligibility,
  transferCashOutEarningsToRoyalty,
} from "../services/cashOut/cashOutCashWalletWithdrawalService";
import {
  CASH_OUT_WITHDRAWAL_MIN,
  CASH_OUT_WITHDRAWAL_STEP,
  getTotalCashOutAvailable,
  maxCashOutWithdrawalRequest,
} from "../config/cashOutWithdrawalConfig";
import { CURRENT_ACTIVATION_FEE } from "../config/membershipConfig";
import { getAutoFillCoPointsWallet } from "../utils/autoFillCoPointsWallet";

const WalletView = ({
  user,
  userProfile,
  onBack,
  onRefreshProfile,
  primaryBlue,
  glassStyles,
}) => {
  // State variables
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [referralStats, setReferralStats] = useState(null);
  const [referralLoading, setReferralLoading] = useState(true);
  const [withdrawDialogOpen, setWithdrawDialogOpen] = useState(false);
  const [withdrawAmount, setWithdrawAmount] = useState(0);
  const [withdrawLoading, setWithdrawLoading] = useState(false);
  const [pendingWithdrawal, setPendingWithdrawal] = useState(null);
  const [checkingPending, setCheckingPending] = useState(true);
  
  // Royalty withdrawal states
  const [royaltyWithdrawDialogOpen, setRoyaltyWithdrawDialogOpen] = useState(false);
  const [royaltyWithdrawAmount, setRoyaltyWithdrawAmount] = useState(0);
  const [royaltyWithdrawLoading, setRoyaltyWithdrawLoading] = useState(false);
  const [pendingRoyaltyWithdrawal, setPendingRoyaltyWithdrawal] = useState(null);
  const [checkingRoyaltyPending, setCheckingRoyaltyPending] = useState(true);
  const [showAutoFillProgram, setShowAutoFillProgram] = useState(false);
  const [showCashOutProgram, setShowCashOutProgram] = useState(false);
  const [cashOutPrograms, setCashOutPrograms] = useState([]);
  const [cashOutProgramsLoading, setCashOutProgramsLoading] = useState(true);
  const [copTransferDialogOpen, setCopTransferDialogOpen] = useState(false);
  const [copTransferAmount, setCopTransferAmount] = useState(0);
  const [copTransferLoading, setCopTransferLoading] = useState(false);
  const [copTransferEligibility, setCopTransferEligibility] = useState(null);
  const [checkingCopEligibility, setCheckingCopEligibility] = useState(true);

  const cardGlassStyles = {
    background: "rgba(255, 255, 255, 0.15)",
    backdropFilter: "blur(15px)",
    border: "1px solid rgba(255, 255, 255, 0.2)",
    borderRadius: "20px",
    boxShadow: "0 8px 32px rgba(11, 28, 72, 0.2)",
    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: "0 16px 48px rgba(11, 28, 72, 0.35)",
      background: "rgba(255, 255, 255, 0.25)",
    },
  };

  // Helper functions
  const getActualCommissionBalance = () => {
    // Priority: Use the calculated total from commissions if available (most accurate)
    // Fall back to userProfile balance fields
    if (referralStats?.confirmedEarnings > 0) {
      return referralStats.confirmedEarnings;
    }
    const newSystemBalance = userProfile?.commissionBalance || 0;
    const oldSystemBalance = userProfile?.confirmedCommissions || 0;
    return newSystemBalance > 0 ? newSystemBalance : oldSystemBalance;
  };

  // CES Token helper function
  const getCesCoinsBalance = () => {
    // Only show confirmed CES balance from Firebase (userProfile)
    // Don't use localStorage cache as it may contain unconfirmed transfers
    return userProfile?.cesCoins || 0;
  };

  // Royalty Balance helper function
  const getRoyaltyBalance = () => {
    return userProfile?.royaltyBalance || 0;
  };

  // NOTE: CES Token cleanup code removed - feature is now enabled
  // CES tokens are now properly managed through admin approval workflow
  // Users can see their CES token balance and transfer from cart page

  const getActualCommissionEarned = () => {
    // Priority: Use the calculated total from commissions if available (most accurate)
    if (referralStats?.totalEarnings > 0) {
      return referralStats.totalEarnings;
    }
    const newSystemEarned = userProfile?.commissionEarned || 0;
    const oldSystemEarned = userProfile?.totalCommissions || 0;
    return newSystemEarned > 0 ? newSystemEarned : oldSystemEarned;
  };

  // Calculate maximum withdrawal amount (round down to nearest hundred)
  const calculateMaxWithdrawal = (balance) => {
    if (balance < 100) return 0;
    return Math.floor(balance / 100) * 100;
  };

  // Calculate maximum royalty withdrawal amount (round down to nearest hundred)
  const calculateMaxRoyaltyWithdrawal = (balance) => {
    if (balance < 500) return 0;
    return Math.floor(balance / 100) * 100;
  };

  // Handle royalty withdraw button click
  const handleRoyaltyWithdrawClick = () => {
    const balance = getRoyaltyBalance();
    const maxWithdrawal = calculateMaxRoyaltyWithdrawal(balance);
    setRoyaltyWithdrawAmount(maxWithdrawal);
    setRoyaltyWithdrawDialogOpen(true);
  };

  // Handle royalty withdrawal submission
  const handleRoyaltyWithdrawSubmit = async () => {
    try {
      setRoyaltyWithdrawLoading(true);
      setError("");

      const balance = getRoyaltyBalance();
      const maxWithdrawal = calculateMaxRoyaltyWithdrawal(balance);

      if (royaltyWithdrawAmount < 500) {
        setError("Minimum withdrawal amount is ₹500");
        return;
      }

      if (royaltyWithdrawAmount > maxWithdrawal) {
        setError(`Maximum withdrawal amount is ₹${maxWithdrawal} (your balance rounded down to nearest hundred)`);
        return;
      }

      if (royaltyWithdrawAmount % 100 !== 0) {
        setError("Withdrawal amount must be in complete hundreds (100, 200, 300, etc.)");
        return;
      }

      // Create withdrawal request
      const withdrawalRequest = {
        userId: user.uid,
        userEmail: user.email,
        userName: userProfile?.name || "User",
        withdrawalAmount: royaltyWithdrawAmount,
        currentBalance: balance,
        status: "pending",
        requestType: "royalty_withdrawal",
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };

      const requestRef = await addDoc(collection(db, "royaltyWithdrawalRequests"), withdrawalRequest);

      setSuccess(`Cash Wallet withdrawal request of ₹${royaltyWithdrawAmount} submitted successfully! Admin will review your request.`);
      setRoyaltyWithdrawDialogOpen(false);
      setRoyaltyWithdrawAmount(0);
      
      // Update pending withdrawal state
      setPendingRoyaltyWithdrawal({
        id: requestRef.id,
        ...withdrawalRequest,
      });

      // Call Firebase Function to send email notification
      try {
        const { httpsCallable, functions } = await import("../firebase/config");
        const notifyWithdrawalRequest = httpsCallable(functions, 'notifyRoyaltyWithdrawalRequest');
        await notifyWithdrawalRequest({
          userId: user.uid,
          userEmail: user.email,
          userName: userProfile?.name || "User",
          withdrawalAmount: royaltyWithdrawAmount,
          currentBalance: balance,
          requestId: requestRef.id,
        });
      } catch (emailError) {
        console.error("Failed to send email notification:", emailError);
        // Don't fail the withdrawal request if email fails
      }
    } catch (err) {
      console.error("Error submitting royalty withdrawal request:", err);
      setError("Failed to submit withdrawal request: " + err.message);
    } finally {
      setRoyaltyWithdrawLoading(false);
    }
  };

  // Handle withdraw button click
  const handleWithdrawClick = () => {
    const balance = getCesCoinsBalance();
    const maxWithdrawal = calculateMaxWithdrawal(balance);
    setWithdrawAmount(maxWithdrawal);
    setWithdrawDialogOpen(true);
  };

  // Handle withdrawal submission
  const handleWithdrawSubmit = async () => {
    try {
      setWithdrawLoading(true);
      setError("");

      const balance = getCesCoinsBalance();
      const maxWithdrawal = calculateMaxWithdrawal(balance);

      if (withdrawAmount < 100) {
        setError("Minimum withdrawal amount is 100 Cashout Points");
        return;
      }

      if (withdrawAmount > maxWithdrawal) {
        setError(`Maximum withdrawal amount is ${maxWithdrawal} Cashout Points (your balance rounded down to nearest hundred)`);
        return;
      }

      if (withdrawAmount % 100 !== 0) {
        setError("Withdrawal amount must be in complete hundreds (100, 200, 300, etc.)");
        return;
      }

      // Create withdrawal request
      const withdrawalRequest = {
        userId: user.uid,
        userEmail: user.email,
        userName: userProfile?.name || "User",
        withdrawalAmount: withdrawAmount,
        currentBalance: balance,
        status: "pending",
        requestType: "ces_token_withdrawal",
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };

      const requestRef = await addDoc(collection(db, "cesWithdrawalRequests"), withdrawalRequest);

      setSuccess(`Withdrawal request of ${withdrawAmount} Cashout Points submitted successfully! Admin will review your request.`);
      setWithdrawDialogOpen(false);
      setWithdrawAmount(0);
      
      // Update pending withdrawal state
      setPendingWithdrawal({
        id: requestRef.id,
        ...withdrawalRequest,
      });

      // Call Firebase Function to send email notification
      try {
        const { httpsCallable, functions } = await import("../firebase/config");
        const notifyWithdrawalRequest = httpsCallable(functions, 'notifyCesWithdrawalRequest');
        await notifyWithdrawalRequest({
          userId: user.uid,
          userEmail: user.email,
          userName: userProfile?.name || "User",
          withdrawalAmount: withdrawAmount,
          currentBalance: balance,
          requestId: requestRef.id,
        });
      } catch (emailError) {
        console.error("Failed to send email notification:", emailError);
        // Don't fail the withdrawal request if email fails
      }
    } catch (err) {
      console.error("Error submitting withdrawal request:", err);
      setError("Failed to submit withdrawal request: " + err.message);
    } finally {
      setWithdrawLoading(false);
    }
  };

  // Fetch referral statistics
  const fetchReferralStats = async () => {
    try {
      setReferralLoading(true);

      const commissionsQuery = query(
        collection(db, "commissions"),
        where("referrerId", "==", user.uid),
        where("isMultiLevelCommission", "==", true),
        orderBy("createdAt", "desc")
      );
      const commissionsSnapshot = await getDocs(commissionsQuery);
      const commissions = commissionsSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      const totalEarnings = commissions.reduce(
        (sum, commission) => sum + (commission.commissionAmount || 0),
        0
      );

      const confirmedEarnings = commissions
        .filter((c) => c.status === "confirmed")
        .reduce(
          (sum, commission) => sum + (commission.commissionAmount || 0),
          0
        );

      const pendingEarnings = commissions
        .filter((c) => c.status === "pending")
        .reduce(
          (sum, commission) => sum + (commission.commissionAmount || 0),
          0
        );

      const levelBreakdown = commissions.reduce((breakdown, commission) => {
        const level = commission.commissionLevel;
        if (!breakdown[level]) {
          breakdown[level] = {
            level,
            count: 0,
            totalAmount: 0,
            commissionRate: commission.commissionPercentage,
          };
        }
        breakdown[level].count++;
        breakdown[level].totalAmount += commission.commissionAmount;
        return breakdown;
      }, {});

      const myReferralsQuery = query(
        collection(db, "referrals"),
        where("referrerId", "==", user.uid),
        orderBy("createdAt", "desc")
      );
      const myReferralsSnapshot = await getDocs(myReferralsQuery);
      const myReferrals = myReferralsSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      const isEligibleForCommissions =
        userProfile?.hasPurchasedPackage || false;

      // Spendable balance: user profile is updated on earn/spend; commission sum can drift.
      const commissionUsed = userProfile?.commissionUsed || 0;
      const profileBalance = Number(userProfile?.commissionBalance) || 0;
      const calculatedBalance = confirmedEarnings - commissionUsed;
      const actualCommissionBalance = Math.max(
        0,
        profileBalance,
        calculatedBalance
      );

      const stats = {
        totalEarnings: totalEarnings,
        pendingEarnings: pendingEarnings,
        confirmedEarnings: confirmedEarnings,
        totalCommissions: commissions.length,
        confirmedCommissions: commissions.filter(
          (c) => c.status === "confirmed"
        ).length,
        pendingCommissions: commissions.filter((c) => c.status === "pending")
          .length,
        isEligibleForCommissions,
        commissionHistory: commissions,
        levelBreakdown,
        referralHistory: myReferrals,
        packagePurchaseRequired: !isEligibleForCommissions,
        actualCommissionBalance,
        actualCommissionEarned: Math.max(
          confirmedEarnings,
          Number(userProfile?.commissionEarned) || 0
        ),
      };

      setReferralStats(stats);
    } catch (error) {
      console.error("Error fetching referral stats:", error);
      setReferralStats({
        totalEarnings: 0,
        pendingEarnings: 0,
        confirmedEarnings: 0,
        totalCommissions: 0,
        confirmedCommissions: 0,
        pendingCommissions: 0,
        isEligibleForCommissions: false,
        commissionHistory: [],
        levelBreakdown: {},
        referralHistory: [],
        packagePurchaseRequired: true,
        actualCommissionBalance: 0,
        actualCommissionEarned: 0,
      });
    } finally {
      setReferralLoading(false);
    }
  };

  // Refresh user balance
  const refreshUserBalance = async () => {
    try {
      setLoading(true);

      const userQuery = query(
        collection(db, "users"),
        where("uid", "==", user.uid)
      );
      const userSnapshot = await getDocs(userQuery);

      if (!userSnapshot.empty) {
        setSuccess("Balance information refreshed!");
      }
    } catch (error) {
      setError("Failed to refresh balance information: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "Not available";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Effects - fetch referral stats first
  useEffect(() => {
    fetchReferralStats();
  }, [user.uid]);

  useEffect(() => {
    let cancelled = false;
    const loadCashOutPrograms = async () => {
      if (!user?.uid) {
        setCashOutPrograms([]);
        setCashOutProgramsLoading(false);
        return;
      }
      setCashOutProgramsLoading(true);
      try {
        const programs = await listCashOutProgramsForUser(user.uid);
        if (!cancelled) setCashOutPrograms(programs);
      } catch (e) {
        console.error("Failed to load Cash Out programs", e);
        if (!cancelled) setCashOutPrograms([]);
      } finally {
        if (!cancelled) setCashOutProgramsLoading(false);
      }
    };
    loadCashOutPrograms();
    return () => {
      cancelled = true;
    };
  }, [user?.uid]);

  // Check for pending withdrawal requests
  useEffect(() => {
    const checkPendingWithdrawal = async () => {
      if (!user?.uid) {
        setCheckingPending(false);
        return;
      }

      try {
        setCheckingPending(true);
        // Query for pending withdrawals for this user
        const withdrawalsQuery = query(
          collection(db, "cesWithdrawalRequests"),
          where("userId", "==", user.uid),
          where("status", "==", "pending")
        );
        const snapshot = await getDocs(withdrawalsQuery);
        
        if (!snapshot.empty) {
          // Get the most recent pending withdrawal
          const withdrawals = snapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }));
          
          // Sort by createdAt descending and get the most recent
          const sorted = withdrawals.sort((a, b) => {
            const aTime = a.createdAt?.toDate?.() || new Date(a.createdAt || 0);
            const bTime = b.createdAt?.toDate?.() || new Date(b.createdAt || 0);
            return bTime - aTime;
          });
          
          setPendingWithdrawal(sorted[0]);
        } else {
          setPendingWithdrawal(null);
        }
      } catch (error) {
        console.error("Error checking pending withdrawal:", error);
        // If query fails (e.g., missing index), don't block the UI
        // Just assume no pending withdrawal
        setPendingWithdrawal(null);
      } finally {
        setCheckingPending(false);
      }
    };

    checkPendingWithdrawal();
    
    // Check every 10 seconds for status updates
    const interval = setInterval(checkPendingWithdrawal, 10000);

    return () => clearInterval(interval);
  }, [user?.uid]);

  // Check for pending royalty withdrawal requests
  useEffect(() => {
    const checkPendingRoyaltyWithdrawal = async () => {
      if (!user?.uid) {
        setCheckingRoyaltyPending(false);
        return;
      }

      try {
        setCheckingRoyaltyPending(true);
        // Query for pending royalty withdrawals for this user
        const withdrawalsQuery = query(
          collection(db, "royaltyWithdrawalRequests"),
          where("userId", "==", user.uid),
          where("status", "==", "pending")
        );
        const snapshot = await getDocs(withdrawalsQuery);
        
        if (!snapshot.empty) {
          // Get the most recent pending withdrawal
          const withdrawals = snapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }));
          
          // Sort by createdAt descending and get the most recent
          const sorted = withdrawals.sort((a, b) => {
            const aTime = a.createdAt?.toDate?.() || new Date(a.createdAt || 0);
            const bTime = b.createdAt?.toDate?.() || new Date(b.createdAt || 0);
            return bTime - aTime;
          });
          
          setPendingRoyaltyWithdrawal(sorted[0]);
        } else {
          setPendingRoyaltyWithdrawal(null);
        }
      } catch (error) {
        console.error("Error checking pending royalty withdrawal:", error);
        setPendingRoyaltyWithdrawal(null);
      } finally {
        setCheckingRoyaltyPending(false);
      }
    };

    checkPendingRoyaltyWithdrawal();
    
    // Check every 10 seconds for status updates
    const interval = setInterval(checkPendingRoyaltyWithdrawal, 10000);

    return () => clearInterval(interval);
  }, [user?.uid]);

  useEffect(() => {
    const run = async () => {
      if (!user?.uid) {
        setCheckingCopEligibility(false);
        return;
      }
      try {
        setCheckingCopEligibility(true);
        const elig = await getCashOutTransferToRoyaltyEligibility(user.uid);
        setCopTransferEligibility(elig);
      } catch (e) {
        console.error("COP transfer eligibility check failed", e);
        setCopTransferEligibility(null);
      } finally {
        setCheckingCopEligibility(false);
      }
    };
    run();
  }, [
    user?.uid,
    userProfile?.cashOutCashWallet?.available,
    userProfile?.cashOutBox?.available,
  ]);

  const autoFillPoints = getAutoFillCoPointsWallet(userProfile);
  const cashOutBox = userProfile?.cashOutBox || {
    earned: 0,
    available: 0,
    withdrawn: 0,
  };
  const cashOutCashWallet = userProfile?.cashOutCashWallet || {
    earned: 0,
    available: 0,
    withdrawn: 0,
  };

  const copTotalAvailable = getTotalCashOutAvailable(
    cashOutBox,
    cashOutCashWallet
  );
  const copMaxWithdrawRequest = maxCashOutWithdrawalRequest(
    cashOutBox,
    cashOutCashWallet
  );

  const handleCopTransferClick = () => {
    setCopTransferAmount(copMaxWithdrawRequest);
    setCopTransferDialogOpen(true);
  };

  const handleCopTransferSubmit = async () => {
    try {
      setCopTransferLoading(true);
      setError("");
      const result = await transferCashOutEarningsToRoyalty({
        uid: user.uid,
        userEmail: user.email,
        userName: userProfile?.name,
        amount: copTransferAmount,
      });
      setSuccess(
        `₹${result.amount} moved to Cash Wallet. Use the Cash Wallet card below to request payout (admin approval).`
      );
      setCopTransferDialogOpen(false);
      if (onRefreshProfile) {
        await onRefreshProfile();
      }
      const elig = await getCashOutTransferToRoyaltyEligibility(user.uid);
      setCopTransferEligibility(elig);
    } catch (err) {
      setError(err.message || "Failed to transfer");
    } finally {
      setCopTransferLoading(false);
    }
  };

  const walletSubViewShellSx = {
    position: "relative",
    zIndex: 1,
    userSelect: "text",
    WebkitUserSelect: "text",
  };

  if (showAutoFillProgram) {
    return (
      <Box sx={walletSubViewShellSx}>
        <AutoFillProgramDashboard
          user={user}
          userProfile={userProfile}
          onBack={() => setShowAutoFillProgram(false)}
          backLabel="Back to Wallet & Points"
        />
      </Box>
    );
  }

  if (showCashOutProgram) {
    return (
      <Box sx={walletSubViewShellSx}>
        <CashOutProgramDashboard
          user={user}
          userProfile={userProfile}
          onBack={() => setShowCashOutProgram(false)}
          backLabel="Back to Wallet & Points"
        />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: `linear-gradient(135deg, 
        #F0F8FF 0%, 
        #E6F0FA 25%, 
        #C5DDF5 50%, 
        #E6F0FA 75%, 
        #F0F8FF 100%)`,
        position: "relative",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `
          radial-gradient(circle at 20% 50%, rgba(0, 114, 206, 0.1) 0%, transparent 50%),
          radial-gradient(circle at 80% 20%, rgba(11, 28, 72, 0.1) 0%, transparent 50%),
          radial-gradient(circle at 40% 80%, rgba(0, 34, 102, 0.1) 0%, transparent 50%)
        `,
          zIndex: 0,
          pointerEvents: "none",
        },
      }}
    >
      <Box sx={{ position: "relative", zIndex: 1 }}>
        {/* Navigation Bar */}
        <Box sx={{ ...glassStyles, m: 2, p: { xs: 2, sm: 3 } }}>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <IconButton
              onClick={onBack}
              sx={{
                mr: { xs: 1, sm: 2 },
                bgcolor: "#1E40AF",
                color: "white",
                "&:hover": { bgcolor: "#001A4D" },
              }}
            >
              <ArrowBack />
            </IconButton>
            <Typography
              variant={{ xs: "h5", sm: "h4" }}
              sx={{
                fontWeight: 700,
                background: `linear-gradient(45deg, #001A4D, #1E40AF)`,
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              Wallet & Points
            </Typography>
          </Box>
        </Box>

        <Container maxWidth="lg" sx={{ pb: 4 }}>
          {/* Alerts */}
          {error && (
            <Alert severity="error" sx={{ mb: 2, ...glassStyles }}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 2, ...glassStyles }}>
              {success}
            </Alert>
          )}

          {/* Wallet & Benefits Section */}
          <Paper
            elevation={0}
            sx={{
              ...glassStyles,
              p: 3,
              bgcolor: "rgba(255, 255, 255, 0.8)",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 3,
              }}
            >
              <Typography variant="h6">
                <Wallet sx={{ mr: 1, verticalAlign: "middle" }} />
                Wallet & referral balances
              </Typography>
              <Button
                variant="outlined"
                size="small"
                onClick={() => {
                  refreshUserBalance();
                  fetchReferralStats();
                }}
                disabled={loading || referralLoading}
              >
                {loading || referralLoading ? (
                  <CircularProgress size={16} />
                ) : (
                  "Refresh"
                )}
              </Button>
            </Box>

            {referralLoading ? (
              <Box sx={{ textAlign: "center", py: 4 }}>
                <CircularProgress />
                <Typography variant="body2" sx={{ mt: 2 }}>
                  Loading wallet information...
                </Typography>
              </Box>
            ) : (
              <>
                {/* Lifetime Earnings Summary */}
                <Card
                  variant="outlined"
                  sx={{
                    mb: 3,
                    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                    color: "white",
                  }}
                >
                  <CardContent>
                    <Typography
                      variant="h6"
                      gutterBottom
                      sx={{ display: "flex", alignItems: "center", fontWeight: 700 }}
                    >
                      <TrendingUp sx={{ mr: 1 }} />
                      Lifetime Earnings Summary
                    </Typography>
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                      <Grid item xs={6} md={3}>
                        <Box sx={{ textAlign: "center" }}>
                          <Typography variant="h5" sx={{ fontWeight: 700 }}>
                            ₹{(userProfile?.cashbackEarned || 0).toFixed(2)}
                          </Typography>
                          <Typography variant="caption">Total Cashback Earned</Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={6} md={3}>
                        <Box sx={{ textAlign: "center" }}>
                          <Typography variant="h5" sx={{ fontWeight: 700 }}>
                            {(referralStats?.actualCommissionEarned || 0).toFixed(2)} pts
                          </Typography>
                          <Typography variant="caption">Total Points Wallet Earned</Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={6} md={3}>
                        <Box sx={{ textAlign: "center" }}>
                          <Typography variant="h5" sx={{ fontWeight: 700 }}>
                            {(userProfile?.cesCoins || 0).toFixed(2)}
                          </Typography>
                          <Typography variant="caption">Cashout Points</Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={6} md={3}>
                        <Box sx={{ textAlign: "center" }}>
                          <Typography variant="h5" sx={{ fontWeight: 700 }}>
                            ₹{(userProfile?.royaltyBalance || 0).toFixed(2)}
                          </Typography>
                          <Typography variant="caption">Cash Wallet</Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>

                <Grid container spacing={3}>
                  {/* Cashback Balance Card */}
                  <Grid item xs={12} md={6}>
                    <Card variant="outlined" sx={{ height: "100%" }}>
                      <CardContent>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            mb: 2,
                          }}
                        >
                          <CardGiftcard
                            sx={{ color: "#00BFFF", mr: 1, fontSize: 28 }}
                          />
                          <Typography
                            variant="h6"
                            sx={{ color: "#00BFFF", fontWeight: 600 }}
                          >
                            Cashback Balance
                          </Typography>
                        </Box>

                        <Typography
                          variant="h4"
                          sx={{ color: "#00BFFF", fontWeight: 700, mb: 1 }}
                        >
                          ₹{(userProfile?.cashbackBalance || 0).toFixed(2)}
                        </Typography>

                        <Typography
                          variant="body2"
                          color="text.secondary"
                          gutterBottom
                        >
                          Total Earned: ₹
                          {(userProfile?.cashbackEarned || 0).toFixed(2)}
                        </Typography>

                        <Typography
                          variant="body2"
                          color="text.secondary"
                          gutterBottom
                        >
                          Total Used: ₹
                          {(userProfile?.cashbackUsed || 0).toFixed(2)}
                        </Typography>

                        <Divider sx={{ my: 2 }} />

                        <Typography variant="caption" color="text.secondary">
                          At checkout, cashback applies only up to the profit
                          margin on referral-eligible items in your cart (not your
                          full balance).
                        </Typography>

                        {userProfile?.cashbackBalance === 0 && (
                          <Alert severity="info" sx={{ mt: 2 }}>
                            <Typography variant="body2">
                              📦 Purchase your first package to receive ₹
                              {CURRENT_ACTIVATION_FEE} cashback!
                            </Typography>
                          </Alert>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>

                  {/* Commission Balance Card */}
                  <Grid item xs={12} md={6}>
                    <Card variant="outlined" sx={{ height: "100%" }}>
                      <CardContent>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            mb: 2,
                          }}
                        >
                          <TrendingUp
                            sx={{ color: "#1E40AF", mr: 1, fontSize: 28 }}
                          />
                          <Typography
                            variant="h6"
                            sx={{ color: "#1E40AF", fontWeight: 600 }}
                          >
                            Points Wallet
                          </Typography>
                        </Box>

                        <Typography
                          variant="h4"
                          sx={{ color: "#1E40AF", fontWeight: 700, mb: 1 }}
                        >
                          {(
                            referralStats?.actualCommissionBalance || 0
                          ).toFixed(2)} pts
                        </Typography>

                        <Typography
                          variant="body2"
                          color="text.secondary"
                          gutterBottom
                        >
                          Total Earned:{" "}
                          {(referralStats?.actualCommissionEarned || 0).toFixed(
                            2
                          )} pts
                        </Typography>

                        <Typography
                          variant="body2"
                          color="text.secondary"
                          gutterBottom
                        >
                          Total Used:{" "}
                          {(userProfile?.commissionUsed || 0).toFixed(2)} pts
                        </Typography>

                        <Typography
                          variant="body2"
                          color="text.secondary"
                          gutterBottom
                        >
                          Points Wallet records: {referralStats?.totalCommissions || 0}
                        </Typography>

                        <Divider sx={{ my: 2 }} />

                        {!referralStats?.isEligibleForCommissions && (
                          <Box sx={{ mt: 2 }}>
                            <Chip
                              label="Package Purchase Required"
                              color="warning"
                              size="small"
                            />
                          </Box>
                        )}

                        {referralStats?.actualCommissionBalance === 0 && (
                          <Alert severity="info" sx={{ mt: 2 }}>
                            <Typography variant="body2">
                              👥 Refer people who purchase packages to earn
                              commissions!
                            </Typography>
                          </Alert>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid item xs={12}>
                    <MembershipVoucherPanel
                      user={user}
                      userProfile={userProfile}
                      pointsBalance={
                        referralStats?.actualCommissionBalance ??
                        userProfile?.commissionBalance ??
                        0
                      }
                      onBalanceChange={() => {
                        fetchReferralStats();
                        onRefreshProfile?.();
                      }}
                      primaryBlue={primaryBlue}
                    />
                  </Grid>

                  {/* CES / Cashout Points card — hidden per product request (cesCoins still in data) */}
                  {false && (
                  <Grid item xs={12} md={6}>
                    <Card variant="outlined" sx={{ height: "100%" }}>
                      <CardContent>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            mb: 2,
                          }}
                        >
                          <CardGiftcard
                            sx={{ color: "#D4AF37", mr: 1, fontSize: 28 }}
                          />
                          <Typography
                            variant="h6"
                            sx={{ color: "#D4AF37", fontWeight: 600 }}
                          >
                            Cashout Points
                          </Typography>
                        </Box>

                        <Typography
                          variant="h4"
                          sx={{ color: "#D4AF37", fontWeight: 700, mb: 1 }}
                        >
                          {getCesCoinsBalance().toFixed(2)} Cashout Points
                        </Typography>

                        <Typography
                          variant="body2"
                          color="text.secondary"
                          gutterBottom
                        >
                          Special cashout points
                        </Typography>

                        {getCesCoinsBalance() === 0 && (
                          <Alert severity="info" sx={{ mt: 2 }}>
                            <Typography variant="body2">
                              ⭐ Transfer up to 50–100% of Points Wallet to Cashout Points from the cart (by level).
                            </Typography>
                          </Alert>
                        )}

                        {getCesCoinsBalance() > 0 && (
                          <>
                            <Alert severity="success" sx={{ mt: 2, mb: 2 }}>
                              <Typography variant="body2">
                                🎉 You have Cashout Points available for special benefits!
                              </Typography>
                            </Alert>
                            {pendingWithdrawal ? (
                              <Alert severity="warning" sx={{ mt: 1 }}>
                                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                                  ⏳ Withdrawal Request Pending
                                </Typography>
                                <Typography variant="body2">
                                  You have a pending withdrawal request of <strong>{pendingWithdrawal.withdrawalAmount} Cashout Points</strong>.
                                  <br />
                                  Please wait for admin approval. The button will be enabled again once your request is processed.
                                </Typography>
                              </Alert>
                            ) : (
                              <>
                                {calculateMaxWithdrawal(getCesCoinsBalance()) >= 100 && (
                                  <Button
                                    variant="contained"
                                    fullWidth
                                    startIcon={<AccountBalance />}
                                    onClick={handleWithdrawClick}
                                    disabled={checkingPending || pendingWithdrawal !== null}
                                    sx={{
                                      mt: 1,
                                      bgcolor: "#D4AF37",
                                      "&:hover": {
                                        bgcolor: "#f57c00",
                                      },
                                      "&:disabled": {
                                        bgcolor: "rgba(0, 0, 0, 0.12)",
                                        color: "rgba(0, 0, 0, 0.26)",
                                      },
                                    }}
                                  >
                                    {checkingPending ? "Checking..." : "Withdraw Cashout Points"}
                                  </Button>
                                )}
                                {calculateMaxWithdrawal(getCesCoinsBalance()) < 100 && (
                                  <Alert severity="info" sx={{ mt: 1 }}>
                                    <Typography variant="body2">
                                      Minimum withdrawal: 100 Cashout Points. You need {100 - getCesCoinsBalance()} more to withdraw.
                                    </Typography>
                                  </Alert>
                                )}
                              </>
                            )}
                          </>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                  )}

                  {userProfile?.hasPurchasedPackage && (
                    <Grid item xs={12} md={6}>
                      <Card variant="outlined" sx={{ height: "100%" }}>
                        <CardContent>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              mb: 2,
                            }}
                          >
                            <AccountTree
                              sx={{ color: "#0D9488", mr: 1, fontSize: 28 }}
                            />
                            <Typography
                              variant="h6"
                              sx={{ color: "#0D9488", fontWeight: 600 }}
                            >
                              Auto-Fill Program
                            </Typography>
                          </Box>

                          <Typography
                            variant="h4"
                            sx={{ color: "#0D9488", fontWeight: 700, mb: 1 }}
                          >
                            ₹{(autoFillPoints.available || 0).toFixed(2)}
                          </Typography>
                          {(autoFillPoints.locked || 0) > 0 && (
                            <Typography
                              variant="body2"
                              sx={{ color: "#B45309", mb: 1 }}
                            >
                              ₹{(autoFillPoints.locked || 0).toFixed(2)} pending
                              withdrawal approval · Earned total ₹
                              {(autoFillPoints.earned || 0).toFixed(2)}
                            </Typography>
                          )}

                          <Typography
                            variant="body2"
                            color="text.secondary"
                            gutterBottom
                          >
                            CO Points available · Program ID{" "}
                            {userProfile?.activeAutoFillProgramId || "—"}
                          </Typography>

                          <Typography variant="body2" color="text.secondary">
                            Earned: ₹{(autoFillPoints.earned || 0).toFixed(2)} ·
                            Withdrawn: ₹{(autoFillPoints.withdrawn || 0).toFixed(2)}
                            {(autoFillPoints.locked || 0) > 0
                              ? ` · Pending: ₹${(autoFillPoints.locked || 0).toFixed(2)}`
                              : ""}
                          </Typography>
                          {(autoFillPoints.autoFillCoPoints?.available || 0) > 0 && (
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                              Includes ₹
                              {autoFillPoints.autoFillCoPoints.available.toFixed(2)} genesis
                              admin CO (withdraw from Auto-Fill).
                            </Typography>
                          )}

                          <Button
                            variant="contained"
                            fullWidth
                            startIcon={<AccountTree />}
                            onClick={() => setShowAutoFillProgram(true)}
                            sx={{
                              mt: 2,
                              bgcolor: "#0D9488",
                              "&:hover": { bgcolor: "#0F766E" },
                            }}
                          >
                            Open Auto-Fill Program
                          </Button>
                        </CardContent>
                      </Card>
                    </Grid>
                  )}

                  <Grid item xs={12} md={6}>
                    <Card variant="outlined" sx={{ height: "100%" }}>
                      <CardContent>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            mb: 2,
                          }}
                        >
                          <AccountBalanceWallet
                            sx={{ color: "#7C3AED", mr: 1, fontSize: 28 }}
                          />
                          <Typography
                            variant="h6"
                            sx={{ color: "#7C3AED", fontWeight: 600 }}
                          >
                            Cash Out Program
                          </Typography>
                        </Box>
                        <Typography
                          variant="h4"
                          sx={{ color: "#7C3AED", fontWeight: 700, mb: 0.5 }}
                        >
                          ₹{copTotalAvailable.toFixed(2)}
                        </Typography>
                        <Typography
                          variant="subtitle2"
                          sx={{ color: "#5B21B6", fontWeight: 600, mb: 1 }}
                        >
                          Move up to ₹{copMaxWithdrawRequest.toFixed(0)} to Cash Wallet
                          (multiples of ₹{CASH_OUT_WITHDRAWAL_STEP})
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          gutterBottom
                        >
                          Earnings from your COP matrix. Transfer to Cash Wallet,
                          then withdraw from there (existing admin approval).
                        </Typography>
                        <Button
                          variant="outlined"
                          fullWidth
                          sx={{ mt: 2, borderColor: "#7C3AED", color: "#5B21B6" }}
                          onClick={() => setShowCashOutProgram(true)}
                          disabled={cashOutProgramsLoading}
                        >
                          View program &amp; money flow
                        </Button>
                        {copTransferEligibility && !copTransferEligibility.eligible && (
                          <Alert severity="info" sx={{ mt: 2 }}>
                            {copTransferEligibility.reason}
                          </Alert>
                        )}
                        <Button
                          variant="contained"
                          fullWidth
                          sx={{ mt: 2, bgcolor: "#7C3AED" }}
                          disabled={
                            checkingCopEligibility ||
                            !copTransferEligibility?.eligible
                          }
                          onClick={handleCopTransferClick}
                        >
                          Move to Cash Wallet
                        </Button>
                      </CardContent>
                    </Card>
                  </Grid>

                  {/* Cash Wallet withdrawal card */}
                  <Grid item xs={12} md={6}>
                    <Card variant="outlined" sx={{ height: "100%" }}>
                      <CardContent>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            mb: 2,
                          }}
                        >
                          <AccountBalanceWallet
                            sx={{ color: "#1E40AF", mr: 1, fontSize: 28 }}
                          />
                          <Typography
                            variant="h6"
                            sx={{ color: "#1E40AF", fontWeight: 600 }}
                          >
                            Cash Wallet
                          </Typography>
                        </Box>

                        <Typography
                          variant="h4"
                          sx={{ color: "#1E40AF", fontWeight: 700, mb: 1 }}
                        >
                          ₹{getRoyaltyBalance().toFixed(2)}
                        </Typography>

                        <Typography
                          variant="body2"
                          color="text.secondary"
                          gutterBottom
                        >
                          Earned from referrals
                        </Typography>

                        {getRoyaltyBalance() > 0 && (
                          <>
                            <Alert severity="success" sx={{ mt: 2, mb: 2 }}>
                              <Typography variant="body2">
                                🎉 You have Cash Wallet balance available for withdrawal!
                              </Typography>
                            </Alert>
                            {pendingRoyaltyWithdrawal ? (
                              <Alert severity="warning" sx={{ mt: 1 }}>
                                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                                  ⏳ Withdrawal Request Pending
                                </Typography>
                                <Typography variant="body2">
                                  You have a pending withdrawal request of <strong>₹{pendingRoyaltyWithdrawal.withdrawalAmount}</strong>.
                                  <br />
                                  Please wait for admin approval. The button will be enabled again once your request is processed.
                                </Typography>
                              </Alert>
                            ) : (
                              <>
                                {calculateMaxRoyaltyWithdrawal(getRoyaltyBalance()) >= 500 && (
                                  <Button
                                    variant="contained"
                                    fullWidth
                                    startIcon={<AccountBalanceWallet />}
                                    onClick={handleRoyaltyWithdrawClick}
                                    disabled={checkingRoyaltyPending || pendingRoyaltyWithdrawal !== null}
                                    sx={{
                                      mt: 1,
                                      bgcolor: "#1E40AF",
                                      "&:hover": {
                                        bgcolor: "#7b1fa2",
                                      },
                                      "&:disabled": {
                                        bgcolor: "rgba(0, 0, 0, 0.12)",
                                        color: "rgba(0, 0, 0, 0.26)",
                                      },
                                    }}
                                  >
                                    {checkingRoyaltyPending ? "Checking..." : "Withdraw from Cash Wallet"}
                                  </Button>
                                )}
                                {calculateMaxRoyaltyWithdrawal(getRoyaltyBalance()) < 500 && (
                                  <Alert severity="info" sx={{ mt: 1 }}>
                                    <Typography variant="body2">
                                      Minimum withdrawal: ₹500. You need ₹{500 - getRoyaltyBalance()} more to withdraw.
                                    </Typography>
                                  </Alert>
                                )}
                              </>
                            )}
                          </>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>

                  {/* Account Summary */}
                  <Grid item xs={12}>
                    <Card variant="outlined" sx={{ bgcolor: "grey.50" }}>
                      <CardContent>
                        <Typography
                          variant="h6"
                          gutterBottom
                          sx={{ display: "flex", alignItems: "center" }}
                        >
                          <CheckCircle sx={{ color: "success.main", mr: 1 }} />
                          Account Summary
                        </Typography>

                        <Grid container spacing={2}>
                          <Grid item xs={12} md={4}>
                            <Box>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                <strong>Package Purchase Status:</strong>
                              </Typography>
                              <Chip
                                label={
                                  userProfile?.hasPurchasedPackage
                                    ? "Package Buyer ✅"
                                    : "New User"
                                }
                                color={
                                  userProfile?.hasPurchasedPackage
                                    ? "success"
                                    : "default"
                                }
                                size="small"
                                sx={{ mt: 0.5 }}
                              />
                            </Box>
                          </Grid>

                          <Grid item xs={12} md={4}>
                            <Box>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                <strong>Points Wallet eligibility:</strong>
                              </Typography>
                              <Chip
                                label={
                                  userProfile?.hasPurchasedPackage
                                    ? "Eligible ✅"
                                    : "Purchase Required"
                                }
                                color={
                                  userProfile?.hasPurchasedPackage
                                    ? "success"
                                    : "warning"
                                }
                                size="small"
                                sx={{ mt: 0.5 }}
                              />
                            </Box>
                          </Grid>

                          <Grid item xs={12} md={4}>
                            <Box>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                <strong>Total Referrals Made:</strong>
                              </Typography>
                              <Chip
                                label={`${
                                  userProfile?.totalReferrals || 0
                                } referrals`}
                                color="primary"
                                size="small"
                                sx={{ mt: 0.5 }}
                              />
                            </Box>
                          </Grid>
                        </Grid>

                        <Divider sx={{ my: 2 }} />

                        <Typography variant="body2" color="text.secondary">
                          <strong>Your Referral ID:</strong>{" "}
                          {userProfile?.myReferralId}
                        </Typography>

                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mt: 0.5 }}
                        >
                          <strong>Member Since:</strong>{" "}
                          {formatDate(userProfile?.createdAt)}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>

                  {/* Points History Section */}
                  <Grid item xs={12}>
                    <Card variant="outlined" sx={{ bgcolor: "grey.50" }}>
                      <CardContent>
                        <Typography
                          variant="h6"
                          gutterBottom
                          sx={{ display: "flex", alignItems: "center" }}
                        >
                          <TrendingUp sx={{ color: "#1E40AF", mr: 1 }} />
                          Points Wallet history
                        </Typography>

                        {referralStats?.commissionHistory &&
                        referralStats.commissionHistory.length > 0 ? (
                          <>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ mb: 2 }}
                            >
                              Points Wallet earnings from your referral network
                            </Typography>

                            <Box sx={{ maxHeight: "400px", overflowY: "auto" }}>
                              {referralStats.commissionHistory.map(
                                (commission, index) => (
                                  <Card
                                    key={commission.id || index}
                                    variant="outlined"
                                    sx={{
                                      mb: 1.5,
                                      bgcolor:
                                        commission.status === "confirmed"
                                          ? "rgba(76, 175, 80, 0.05)"
                                          : "rgba(255, 152, 0, 0.05)",
                                      border:
                                        commission.status === "confirmed"
                                          ? "1px solid #00BFFF"
                                          : "1px solid #D4AF37",
                                    }}
                                  >
                                    <CardContent
                                      sx={{ p: 2, "&:last-child": { pb: 2 } }}
                                    >
                                      <Box
                                        sx={{
                                          display: "flex",
                                          justifyContent: "space-between",
                                          alignItems: "center",
                                        }}
                                      >
                                        <Box sx={{ flexGrow: 1 }}>
                                          <Typography
                                            variant="h6"
                                            sx={{
                                              fontWeight: 700,
                                              color:
                                                commission.status ===
                                                "confirmed"
                                                  ? "#2e7d32"
                                                  : "#f57c00",
                                              mb: 0.5,
                                            }}
                                          >
                                            {(
                                              commission.commissionAmount || 0
                                            ).toFixed(2)} pts
                                          </Typography>

                                          <Typography
                                            variant="body1"
                                            color="text.primary"
                                            sx={{ fontWeight: 600 }}
                                          >
                                            From:{" "}
                                            {commission.buyerName ||
                                              "Unknown User"}
                                          </Typography>

                                          <Box
                                            sx={{
                                              display: "flex",
                                              flexWrap: "wrap",
                                              gap: 1,
                                              mt: 1,
                                            }}
                                          >
                                            <Chip
                                              label={`Level ${
                                                commission.commissionLevel ||
                                                "?"
                                              }`}
                                              size="small"
                                              sx={{
                                                bgcolor: primaryBlue[100],
                                                color: primaryBlue[800],
                                                fontWeight: 600,
                                              }}
                                            />

                                            <Chip
                                              label={
                                                commission.status ===
                                                "confirmed"
                                                  ? "Confirmed"
                                                  : "Pending"
                                              }
                                              size="small"
                                              color={
                                                commission.status ===
                                                "confirmed"
                                                  ? "success"
                                                  : "warning"
                                              }
                                            />
                                          </Box>
                                        </Box>

                                        <Box sx={{ textAlign: "right", ml: 2 }}>
                                          <Typography
                                            variant="body2"
                                            color="text.secondary"
                                            sx={{ fontWeight: 500 }}
                                          >
                                            {formatDate(commission.createdAt)}
                                          </Typography>
                                        </Box>
                                      </Box>
                                    </CardContent>
                                  </Card>
                                )
                              )}
                            </Box>

                            {/* Summary Stats */}
                            <Divider sx={{ my: 2 }} />

                            <Grid container spacing={2}>
                              <Grid item xs={12} md={4}>
                                <Box sx={{ textAlign: "center", p: 1 }}>
                                  <Typography variant="h6" color="success.main">
                                    {referralStats.confirmedCommissions || 0}
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                  >
                                    Confirmed (Points Wallet)
                                  </Typography>
                                </Box>
                              </Grid>

                              <Grid item xs={12} md={4}>
                                <Box sx={{ textAlign: "center", p: 1 }}>
                                  <Typography variant="h6" color="warning.main">
                                    {referralStats.pendingCommissions || 0}
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                  >
                                    Pending (Points Wallet)
                                  </Typography>
                                </Box>
                              </Grid>

                              <Grid item xs={12} md={4}>
                                <Box sx={{ textAlign: "center", p: 1 }}>
                                  <Typography variant="h6" color="primary.main">
                                    {(referralStats.totalEarnings || 0).toFixed(
                                      2
                                    )} pts
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                  >
                                    Total Earned
                                  </Typography>
                                </Box>
                              </Grid>
                            </Grid>

                            {/* Level Breakdown */}
                            {referralStats.levelBreakdown &&
                              Object.keys(referralStats.levelBreakdown).length >
                                0 && (
                                <>
                                  <Divider sx={{ my: 2 }} />
                                  <Typography variant="subtitle2" gutterBottom>
                                    📊 Earnings by Level
                                  </Typography>

                                  <Grid container spacing={1}>
                                    {Object.values(referralStats.levelBreakdown)
                                      .sort((a, b) => a.level - b.level)
                                      .map((levelData) => (
                                        <Grid
                                          item
                                          xs={6}
                                          sm={4}
                                          md={3}
                                          key={levelData.level}
                                        >
                                          <Card
                                            variant="outlined"
                                            sx={{ p: 1, textAlign: "center" }}
                                          >
                                            <Typography
                                              variant="caption"
                                              color="text.secondary"
                                            >
                                              Level {levelData.level}
                                            </Typography>
                                            <Typography
                                              variant="body2"
                                              fontWeight={600}
                                            >
                                              {(
                                                levelData.totalAmount || 0
                                              ).toFixed(2)} pts
                                            </Typography>
                                            <Typography
                                              variant="caption"
                                              color="text.secondary"
                                            >
                                              ({levelData.count || 0} records)
                                            </Typography>
                                          </Card>
                                        </Grid>
                                      ))}
                                  </Grid>
                                </>
                              )}
                          </>
                        ) : (
                          <Alert severity="info">
                            <Typography variant="body2">
                              💡 No points history yet. When people you refer
                              make purchases, you'll see your earnings here!
                            </Typography>
                          </Alert>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </>
            )}
          </Paper>
        </Container>
      </Box>

      {/* Cashout Points Withdrawal Dialog */}
      <Dialog
        open={withdrawDialogOpen}
        onClose={() => !withdrawLoading && setWithdrawDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <AccountBalance sx={{ color: "#D4AF37" }} />
            <Typography variant="h6">Withdraw Cashout Points</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {success}
            </Alert>
          )}
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Current Balance: <strong>{getCesCoinsBalance().toFixed(2)} Cashout Points</strong>
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Maximum Withdrawal: <strong>{calculateMaxWithdrawal(getCesCoinsBalance())} Cashout Points</strong>
            </Typography>
            <Alert severity="info" sx={{ mt: 2, mb: 2 }}>
              <Typography variant="body2">
                Withdrawals are only allowed in complete hundreds (100, 200, 300, etc.).
                Your withdrawal request will be reviewed by admin before processing.
              </Typography>
            </Alert>
            <TextField
              fullWidth
              label="Withdrawal Amount (Cashout Points)"
              type="number"
              value={withdrawAmount}
              onChange={(e) => {
                const value = parseInt(e.target.value) || 0;
                const maxWithdrawal = calculateMaxWithdrawal(getCesCoinsBalance());
                if (value <= maxWithdrawal && value >= 0) {
                  setWithdrawAmount(value);
                  setError("");
                } else if (value > maxWithdrawal) {
                  setError(`Maximum withdrawal is ${maxWithdrawal} Cashout Points`);
                }
              }}
              inputProps={{
                min: 100,
                max: calculateMaxWithdrawal(getCesCoinsBalance()),
                step: 100,
              }}
              helperText={`Enter amount in hundreds (100, 200, 300, etc.). Max: ${calculateMaxWithdrawal(getCesCoinsBalance())} Cashout Points`}
              disabled={withdrawLoading}
              sx={{ mt: 2 }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setWithdrawDialogOpen(false);
              setWithdrawAmount(0);
              setError("");
            }}
            disabled={withdrawLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleWithdrawSubmit}
            variant="contained"
            disabled={withdrawLoading || withdrawAmount < 100 || withdrawAmount % 100 !== 0}
            sx={{
              bgcolor: "#D4AF37",
              "&:hover": {
                bgcolor: "#f57c00",
              },
            }}
          >
            {withdrawLoading ? "Submitting..." : "Submit Request"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={copTransferDialogOpen}
        onClose={() => !copTransferLoading && setCopTransferDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Move to Cash Wallet</DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Cash Out program balance:{" "}
            <strong>₹{copTotalAvailable.toFixed(2)}</strong>
          </Typography>
          <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
            Maximum you can move now: ₹{copMaxWithdrawRequest}
          </Typography>
          <Alert severity="info" sx={{ mt: 2, mb: 2 }}>
            This adds to your <strong>Cash Wallet</strong> balance. Then use
            &quot;Withdraw from Cash Wallet&quot; below (min ₹500, admin approval,
            UPI payout).
          </Alert>
          <TextField
            fullWidth
            type="number"
            label="Amount to move (₹)"
            value={copTransferAmount}
            onChange={(e) =>
              setCopTransferAmount(parseInt(e.target.value, 10) || 0)
            }
            inputProps={{
              min: CASH_OUT_WITHDRAWAL_MIN,
              step: CASH_OUT_WITHDRAWAL_STEP,
              max: copMaxWithdrawRequest,
            }}
            helperText={`Enter up to ₹${copMaxWithdrawRequest}`}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setCopTransferDialogOpen(false)}
            disabled={copTransferLoading}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleCopTransferSubmit}
            disabled={
              copTransferLoading ||
              copTransferAmount < CASH_OUT_WITHDRAWAL_MIN ||
              copTransferAmount % CASH_OUT_WITHDRAWAL_STEP !== 0 ||
              copTransferAmount > copMaxWithdrawRequest
            }
          >
            {copTransferLoading ? "Moving…" : "Move to Cash Wallet"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Cash Withdrawal Dialog */}
      <Dialog
        open={royaltyWithdrawDialogOpen}
        onClose={() => !royaltyWithdrawLoading && setRoyaltyWithdrawDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <AccountBalanceWallet sx={{ color: "#1E40AF" }} />
            <Typography variant="h6">Withdraw from Cash Wallet</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {success}
            </Alert>
          )}
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Current Balance: <strong>₹{getRoyaltyBalance().toFixed(2)}</strong>
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Maximum Withdrawal: <strong>₹{calculateMaxRoyaltyWithdrawal(getRoyaltyBalance())}</strong>
            </Typography>
            <Alert severity="info" sx={{ mt: 2, mb: 2 }}>
              <Typography variant="body2">
                Minimum withdrawal: ₹500. Withdrawals are only allowed in complete hundreds (500, 600, 700, etc.).
                Your withdrawal request will be reviewed by admin before processing.
              </Typography>
            </Alert>
            <TextField
              fullWidth
              label="Withdrawal Amount (₹)"
              type="number"
              value={royaltyWithdrawAmount}
              onChange={(e) => {
                const value = parseInt(e.target.value) || 0;
                const maxWithdrawal = calculateMaxRoyaltyWithdrawal(getRoyaltyBalance());
                if (value <= maxWithdrawal && value >= 0) {
                  setRoyaltyWithdrawAmount(value);
                  setError("");
                } else if (value > maxWithdrawal) {
                  setError(`Maximum withdrawal is ₹${maxWithdrawal}`);
                }
              }}
              inputProps={{
                min: 500,
                max: calculateMaxRoyaltyWithdrawal(getRoyaltyBalance()),
                step: 100,
              }}
              helperText={`Enter amount in hundreds (500, 600, 700, etc.). Max: ₹${calculateMaxRoyaltyWithdrawal(getRoyaltyBalance())}`}
              disabled={royaltyWithdrawLoading}
              sx={{ mt: 2 }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setRoyaltyWithdrawDialogOpen(false);
              setRoyaltyWithdrawAmount(0);
              setError("");
            }}
            disabled={royaltyWithdrawLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleRoyaltyWithdrawSubmit}
            variant="contained"
            disabled={royaltyWithdrawLoading || royaltyWithdrawAmount < 500 || royaltyWithdrawAmount % 100 !== 0}
            sx={{
              bgcolor: "#1E40AF",
              "&:hover": {
                bgcolor: "#7b1fa2",
              },
            }}
          >
            {royaltyWithdrawLoading ? "Submitting..." : "Submit Request"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default WalletView;
